import { API } from "@swivl-mono/api-helper";
import { AppInterface } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, Input, Spinner } from "@swivl-mono/ui";
import React from "react";

import { ConversationRestrictionsControls } from "../../../../Components/ConversationRestrictionsControls/ConversationRestrictionsControls";
import { QuestsDropdown } from "../../../../Components/Dropdowns/QuestsDropdown/QuestsDropdown";
import AppsModel from "../../../../Models/Apps/Apps.Model";
import { Member } from "../../../../Models/Member/Member.Current";
import { NavBar } from "../../../../Models/NavBar/NavBar.Model";
import { QuestsModel } from "../../../../Models/Quests/Quests.Model";
import { Field } from "../AppInstanceField";

interface Props {
  app: AppInterface;
  selected?: boolean;
  /** When false, fields are read-only and edits are staged until Save. */
  disabled?: boolean;
}

interface State {
  /** Bot voice app setup (ENG-3468): in flight, and the last outcome to show under the button. */
  conferenceAppBusy: boolean;
  conferenceAppMessage: string;
}

export default class TwilioView extends React.Component<Props, State> {
  unsubscribeQuests?: () => void;
  state: State = { conferenceAppBusy: false, conferenceAppMessage: "" };

  constructor(props) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.handleSetupConferenceApp = this.handleSetupConferenceApp.bind(this);
  }

  /**
   * Create the bot's conference TwiML App in this number's Twilio subaccount and
   * save its SID on this app (ENG-3468). Only this number is stamped; if the org
   * has other numbers on the same subaccount, offer to copy it to them
   * (Chuck, 2026-09-30).
   */
  async handleSetupConferenceApp(): Promise<void> {
    const { app } = this.props;
    if (!Member.orgId || !app?.id) return;
    this.setState({ conferenceAppBusy: true, conferenceAppMessage: "" });
    try {
      const result = await API.TwilioSetup.setupConferenceApp(Member.orgId, app.id, false);
      if (!result.success) {
        this.setState({ conferenceAppMessage: result.error || "Couldn't set up the bot voice app." });
        return;
      }
      if (result.usesPlatformApp) {
        this.setState({ conferenceAppMessage: "This number is on the master Twilio account and uses the platform app — nothing to set up." });
        return;
      }
      let message = `${result.created ? "Created" : "Found"} "${result.friendlyName}" (${result.conferenceTwimlAppSid}) and saved it on this number.`;
      const siblings = result.siblings ?? [];
      if (siblings.length > 0) {
        const list = siblings.map((s) => s.identifier).join(", ");
        const copy = window.confirm(
          `${siblings.length} other number${siblings.length === 1 ? "" : "s"} in this org ${siblings.length === 1 ? "is" : "are"} on the same subaccount (${list}). Copy the bot voice app to ${siblings.length === 1 ? "it" : "them"} too?`
        );
        if (copy) {
          const copied = await API.TwilioSetup.setupConferenceApp(Member.orgId, app.id, true);
          message = copied.success
            ? `${message} Copied to ${Math.max((copied.stampedAppIds?.length ?? 1) - 1, 0)} other number${(copied.stampedAppIds?.length ?? 1) - 1 === 1 ? "" : "s"}.`
            : `${message} Copying to the other numbers failed: ${copied.error ?? "unknown error"}.`;
        }
      }
      await AppsModel.load(true);
      this.setState({ conferenceAppMessage: message });
    } catch (e) {
      this.setState({ conferenceAppMessage: e instanceof Error ? e.message : "Couldn't set up the bot voice app." });
    } finally {
      this.setState({ conferenceAppBusy: false });
    }
  }

  componentWillMount() {
    NavBar.set(<NavBar.Component.Back />, <NavBar.Component.Title> Twilio App Settings</NavBar.Component.Title>, null);
  }

  componentDidMount() {
    QuestsModel.loadQuestsList(false);
    // A class component, so re-render manually when quest names finish loading.
    this.unsubscribeQuests = QuestsModel.useState.subscribe(() => this.forceUpdate());
  }

  componentWillUnmount() {
    this.unsubscribeQuests?.();
  }
  onChange(e) {
    const app = { ...this.props.app };
    const name = e.target.name;
    const value = e.target.value;
    if (name === "phoneNumber") {
      app.identifier = value;
    } else {
      let token = app.token || {};
      token[name] = value;
      app.token = token;
    }

    AppsModel.update(app, false);
  }
  onBlur(e) {
    AppsModel.update(this.props.app, false);
  }

  render() {
    const { app, disabled } = this.props;
    var content;

    if (!app) {
      content = <Spinner />;
    } else {
      const token = app.token || {};
      const phoneNumber = app.identifier || "";
      const startOnQuest = app.data?.startOnQuest || "";

      content = (
        <div className="twilioValues flex flex-col gap-2">
          <Field label="Phone Number (ie. +17205551234)" disabled={disabled} value={phoneNumber}>
            <Input name="phoneNumber" value={phoneNumber} onChange={this.onChange} onBlur={this.onBlur} />
          </Field>
          <Field label="SID" disabled={disabled} value={token.accountSid}>
            <Input name="accountSid" value={token.accountSid} onChange={this.onChange} onBlur={this.onBlur} />
          </Field>
          <Field label="Auth Token" disabled={disabled} value={token.authToken ? "••••••••" : ""}>
            <Input type="password" name="authToken" value={token.authToken} onChange={this.onChange} onBlur={this.onBlur} />
          </Field>
          {/*
          <Input name="apiKey" value={token.apiKey} onChange={this.onChange} onBlur={this.onBlur} />
          <Input type={Input.Type.Password} name="apiSecret" value={token.apiSecret} onChange={this.onChange} onBlur={this.onBlur} /> 
          */}

          {/* <label>Instalation Instructions</label> */}
          {/* <div className="instructions">
                  To complete the instalation, you must set the SMS webhook for your  account to:<br/>
                  <SSCopyToClipboard
                    copyValue={"https://api.swivl.studio/apps/twilio"}
                    copiedComponent={
                      <div className="webhook">https://api.swivl.studio/apps/twilio <span>COPIED</span></div>
                    }
                    >
                    <div className="webhook">https://api.swivl.studio/apps/twilio <span>COPY</span></div>
                    </SSCopyToClipboard>

                  </div> */}

          {Member.isSwivlStaff && (
            <>
              <hr className="mt-5 mb-5" />
              <div className="flex flex-col gap-1">
                <label>Bot voice app (TwiML App in this number's Twilio account)</label>
                <p className="body-sm text-grey-900">
                  {app.data?.conferenceTwimlAppSid
                    ? `Configured — ${app.data.conferenceTwimlAppSid}`
                    : "Not set up — calls to this number cannot reach the bot if it is on a subaccount"}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    color={ButtonColor.WHITE}
                    size={ButtonSize.SMALL}
                    disabled={this.state.conferenceAppBusy}
                    onClickAsync={this.handleSetupConferenceApp}
                  >
                    {this.state.conferenceAppBusy
                      ? "Setting up…"
                      : app.data?.conferenceTwimlAppSid
                        ? "Re-check bot voice app"
                        : "Set up bot voice app"}
                  </Button>
                  {this.state.conferenceAppMessage && <p className="body-sm text-grey-600">{this.state.conferenceAppMessage}</p>}
                </div>
              </div>
            </>
          )}

          <hr className="mt-5 mb-5" />
          {/* <VoiceSpeechModelSelector
            voiceSpeechModel={app.data?.voiceSpeechModel || {}}
            updateVoiceSpeechModel={(data) => {
              const app = { ...this.props.app };
              app.data.voiceSpeechModel = data;
              AppsModel.update(app, true);
            }}
          /> */}
          <Field
            label={
              <>
                Voice Speech Model &nbsp;
                <a
                  href="https://www.twilio.com/docs/voice/conversationrelay/voice-configuration"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontWeight: 400, fontSize: "0.95em" }}
                >
                  (Click to see available models. Copy VoiceID and paste below)
                </a>
              </>
            }
            disabled={disabled}
            value={app.data?.voiceSpeechModel ?? ""}
          >
            <Input
              name="voiceSpeechModel"
              value={app.data?.voiceSpeechModel}
              onChange={(e) => {
                const app = { ...this.props.app };
                app.data.voiceSpeechModel = e.target.value;
                AppsModel.update(app, false);
              }}
              onBlur={this.onBlur}
              placeholder="tnSpp4vdxKPjI9w0GnoV"
            />
          </Field>
          <Field label="Start on Quest" disabled={disabled} value={startOnQuest ? (QuestsModel.nameForQuest(startOnQuest) ?? startOnQuest) : ""}>
            <QuestsDropdown
              value={startOnQuest}
              name="startOnQuest"
              questSelected={(slug) => {
                const app = { ...this.props.app };
                app.data.startOnQuest = slug;
                AppsModel.update(app, false);
              }}
              onClear={() => {
                const app = { ...this.props.app };
                delete app.data.startOnQuest;
                AppsModel.update(app, false);
              }}
            />
          </Field>
          {/* <label>Initial Message</label>
          <Input name="initialMessage" value={app.data?.initialMessage} onChange={(e) => {
            const app = { ...this.props.app };
            app.data.initialMessage = e.target.value;
            AppsModel.update(app, true);
          }} onBlur={this.onBlur} placeholder="This call may be recorded and uses automated voice technology." /> */}
          <ConversationRestrictionsControls
            restrictionsData={app.data?.locationRestrictions || {}}
            updateRestrictionsData={(data) => {
              const app = { ...this.props.app };
              app.data.locationRestrictions = data;
              AppsModel.update(app, false);
            }}
            disabled={disabled}
          />
        </div>
      );
    }

    return <div className="TwilioView">{content}</div>;
  }
}
